import { formatCurrency as angularFormatCurrency, getCurrencySymbol } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, Inject, LOCALE_ID, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { finalize, firstValueFrom } from 'rxjs';
import { CustomDatePipe, CustomMessageService, LocalStorageService, UtilService } from 'library';
import { ALLY_PAYMENT_LABELS, ALLY_PAYMENT_MESSAGES, ALLY_PAYMENT_SEVERITIES, ALLY_PAYMENT_TITLES,
  CERTIFICATE_LOGO_SVG, DEFAULT_SEVERITY, EMPTY_VALUE, LEGALIZATION_LABELS, LEGALIZATION_SEVERITIES, SUMMARY_CARD_DEFINITIONS } from 'projects/store/src/app/constants/reports.constants';
import { LocalStorageKey } from 'projects/store/src/app/enums';
import { AllyPaymentDisplay, ClosingRecord, CreditCertificateDetail, DateRangeFilter, SalesReportCreditDetailResponse, SalesReportCreditItem,
  SalesReportSummaryResponse, StatusDisplay, SummaryCard, UserLocalStorageData } from 'projects/store/src/app/interfaces';
import { ReportService } from 'projects/store/src/app/services/api/v1/report.service';
import { ReportPdfService } from 'projects/store/src/app/services/report-pdf.service';
import { environment } from 'projects/store/src/environments/environment';
import { TableFilterService } from 'projects/library/src/lib/services/table-filter.service';

@Component({
  selector: 'app-closing-report',
  templateUrl: './closing-report.component.html',
  styleUrls: ['./closing-report.component.scss'],
  providers: [CustomDatePipe]
})
export class ClosingReportComponent implements OnInit {
  @ViewChild('certificatePdfContent')
  certificatePdfContent?: ElementRef<HTMLElement>;

  labelBreadcrumb = 'Reporte de cierre';
  breadcrumbLabels = ['Reportes', 'Reporte de cierre'];
  userData: UserLocalStorageData | null = null;
  readonly certificateLogoSvg: SafeHtml;

  summaryCards: SummaryCard[] = this.buildSummaryCards();
  allRecords: ClosingRecord[] = [];
  creditsFilter: DateRangeFilter = {
    from: null,
    to: null
  };

  showCertificateModal = false;
  isLoadingCertificateDetail = false;
  isGeneratingPdf = false;
  certificateDetail: CreditCertificateDetail = this.createEmptyCertificateDetail();

  constructor(
    private reportService: ReportService,
    private localStorageService: LocalStorageService,
    private customMessageService: CustomMessageService,
    private reportPdfService: ReportPdfService,
    private changeDetectorRef: ChangeDetectorRef,
    private domSanitizer: DomSanitizer,
    private utilService: UtilService,
    private tableFilterService: TableFilterService,
    private customDatePipe: CustomDatePipe,
    @Inject(LOCALE_ID) private locale: string
  ) {
    this.certificateLogoSvg = this.domSanitizer.bypassSecurityTrustHtml(CERTIFICATE_LOGO_SVG);
  }

  ngOnInit(): void {
    this.userData = this.localStorageService.get(LocalStorageKey.data);
    this.certificateDetail.agreement = this.getDistributorName();
    this.loadSummary();
    this.loadCredits();
  }

  loadSummary(): void {
    this.reportService.getSalesReportSummary().subscribe({
      next: (response) => {
        const data = this.getResponseData<SalesReportSummaryResponse>(response);

        if (!data?.byStatus || !data?.total) {
          return;
        }

        this.summaryCards = this.buildSummaryCards(data);
      },
      error: (error) => {
        this.customMessageService.responseNoticeHandler(error);
      }
    });
  }

  loadCredits(): void {
    const filters = this.tableFilterService.buildDateRangeFilters(
      this.creditsFilter,
      'contrato_fecha',
      environment.global.country.timezone
    );

    this.utilService.enablePageLoading();

    this.reportService.getSalesReportCredits(filters).pipe(
      finalize(() => {
        this.utilService.disablePageLoading();
      })
    ).subscribe({
      next: (response) => {
        const data = this.getResponseData<{ list: SalesReportCreditItem[] }>(response);
        const records = data?.list || [];

        this.allRecords = records.map((record) => {
          const legalization = this.getLegalizationDisplay(record.status.legalization);
          const allyPayment = this.getAllyPaymentDisplay(record.status.collectionValidity);

          return {
            contrato: record.contract,
            identidad: record.customer.identity || EMPTY_VALUE,
            cliente: record.customer.name || EMPTY_VALUE,
            producto: record.device.description || EMPTY_VALUE,
            imei: record.device.imei || EMPTY_VALUE,
            estado: legalization.label,
            estadoSeverity: legalization.severity,
            pagoAliado: allyPayment.label,
            pagoAliadoSeverity: allyPayment.severity,
            valorTotal: this.formatCurrency(record.financial.totalValue),
            financiado: this.formatCurrency(record.financial.financedAmount),
            anticipo: this.formatCurrency(record.financial.advance),
            fechaSolicitud: this.formatDate(record.timestamps.requestDate),
            ultimaActualizacion: this.formatDate(record.timestamps.lastUpdated)
          };
        });
      },
      error: (error) => {
        this.customMessageService.responseNoticeHandler(error);
      }
    });
  }

  async downloadPdf(record: ClosingRecord): Promise<void> {
    if (this.isLoadingCertificateDetail || this.isGeneratingPdf) {
      return;
    }

    this.certificateDetail = this.buildCertificateDetailFromRecord(record);
    this.certificateDetail.generatedAt = EMPTY_VALUE;

    const detailLoaded = await this.loadCertificateDetail(record);

    if (!detailLoaded) {
      return;
    }

    await this.exportCertificatePdf();
  }

  async exportCertificatePdf(): Promise<void> {
    if (this.isLoadingCertificateDetail || this.isGeneratingPdf) {
      return;
    }

    const printableCertificate = this.certificatePdfContent?.nativeElement;

    if (!printableCertificate) {
      this.customMessageService.error('No fue posible preparar el certificado para descarga.');
      return;
    }

    this.isGeneratingPdf = true;
    this.certificateDetail.generatedAt = this.getCurrentDateTime();
    this.changeDetectorRef.detectChanges();

    try {
      await this.waitForNextRender();
      await this.reportPdfService.generateCertificatePdf(
        printableCertificate,
        this.buildPdfFileName(this.certificateDetail.contract)
      );
    } catch (error) {
      console.error('Error generando PDF del certificado:', error);
      this.customMessageService.error('No fue posible generar el PDF del certificado.');
    } finally {
      this.isGeneratingPdf = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  closeCertificateModal(): void {
    this.showCertificateModal = false;
  }

  onDateFilterChange(filter: DateRangeFilter): void {
    this.creditsFilter = filter;
    this.loadCredits();
  }

  onClearDateFilter(): void {
    this.creditsFilter = {
      from: null,
      to: null
    };
    this.loadCredits();
  }

  openCertificateModal(record: ClosingRecord): void {
    if (this.isLoadingCertificateDetail || this.isGeneratingPdf) {
      return;
    }

    this.certificateDetail = this.buildCertificateDetailFromRecord(record);
    this.certificateDetail.generatedAt = EMPTY_VALUE;
    this.showCertificateModal = true;
    void this.loadCertificateDetail(record);
  }

  private async loadCertificateDetail(record: ClosingRecord): Promise<boolean> {
    this.isLoadingCertificateDetail = true;

    try {
      const response = await firstValueFrom(
        this.reportService.getSalesReportCredit(record.contrato).pipe(
          finalize(() => {
            this.isLoadingCertificateDetail = false;
          })
        )
      );
      const data = this.getResponseData<SalesReportCreditDetailResponse>(response);

      if (data) {
        this.certificateDetail = this.mapCreditCertificateDetail(data, record);
      }

      return true;
    } catch (error) {
      this.customMessageService.responseNoticeHandler(error);
      return false;
    }
  }

  private formatCurrency(value: number | null): string {
    if (value === null || value === undefined) {
      return EMPTY_VALUE;
    }

    const currencyCode = (environment.global.country.currency || 'USD').trim();
    const currencySymbol = getCurrencySymbol(currencyCode, 'narrow');

    return angularFormatCurrency(
      value,
      this.locale,
      currencySymbol,
      currencyCode,
      '1.2-2'
    );
  }

  private formatDate(value: string | null): string {
    if (!value) {
      return EMPTY_VALUE;
    }

    return this.customDatePipe.transform(value, 'short') || EMPTY_VALUE;
  }

  private formatShortDate(value: string | null): string {
    if (!value) {
      return EMPTY_VALUE;
    }

    return this.customDatePipe.transform(value, 'shortDate') || EMPTY_VALUE;
  }

  private formatPlainValue(value: number | null): string {
    if (value === null || value === undefined) {
      return EMPTY_VALUE;
    }

    return `${value}`;
  }

  private pickDisplayValue(primary: string, fallback: string): string {
    return primary && primary !== EMPTY_VALUE ? primary : fallback;
  }

  private getResponseData<T>(response: any): T | null {
    return response?.body?.data || response?.data || null;
  }

  private buildSummaryCards(data?: SalesReportSummaryResponse): SummaryCard[] {
    return SUMMARY_CARD_DEFINITIONS.map((definition) => {
      const summaryItem = definition.statusKey === 'total'
        ? data?.total
        : data?.byStatus?.[definition.statusKey];

      return {
        ...definition,
        count: summaryItem?.count ?? EMPTY_VALUE,
        amount: summaryItem ? this.formatCurrency(summaryItem.amount) : EMPTY_VALUE
      };
    });
  }

  private createEmptyCertificateDetail(): CreditCertificateDetail {
    const allyPayment = this.getAllyPaymentDisplay(null);

    return {
      contract: EMPTY_VALUE,
      customerIdentity: EMPTY_VALUE,
      customerName: EMPTY_VALUE,
      productDescription: EMPTY_VALUE,
      imei: EMPTY_VALUE,
      agreement: this.getDistributorName(),
      totalValue: EMPTY_VALUE,
      financedAmount: EMPTY_VALUE,
      advance: EMPTY_VALUE,
      installments: EMPTY_VALUE,
      installmentValue: EMPTY_VALUE,
      firstInstallmentDate: EMPTY_VALUE,
      creditStatus: EMPTY_VALUE,
      creditStatusSeverity: DEFAULT_SEVERITY,
      requestDate: EMPTY_VALUE,
      lastUpdated: EMPTY_VALUE,
      salesAdvisor: EMPTY_VALUE,
      allyPaymentSeverity: allyPayment.severity,
      allyPaymentTitle: allyPayment.title,
      allyPaymentMessage: allyPayment.message,
      generatedAt: EMPTY_VALUE
    };
  }

  private buildCertificateDetailFromRecord(record: ClosingRecord): CreditCertificateDetail {
    const allyPayment = this.getAllyPaymentDisplay(record.pagoAliado);

    return {
      contract: record.contrato || EMPTY_VALUE,
      customerIdentity: record.identidad || EMPTY_VALUE,
      customerName: record.cliente || EMPTY_VALUE,
      productDescription: record.producto || EMPTY_VALUE,
      imei: record.imei || EMPTY_VALUE,
      agreement: this.getDistributorName(),
      totalValue: record.valorTotal || EMPTY_VALUE,
      financedAmount: record.financiado || EMPTY_VALUE,
      advance: record.anticipo || EMPTY_VALUE,
      installments: EMPTY_VALUE,
      installmentValue: EMPTY_VALUE,
      firstInstallmentDate: EMPTY_VALUE,
      creditStatus: record.estado || EMPTY_VALUE,
      creditStatusSeverity: record.estadoSeverity || DEFAULT_SEVERITY,
      requestDate: record.fechaSolicitud || EMPTY_VALUE,
      lastUpdated: record.ultimaActualizacion || EMPTY_VALUE,
      salesAdvisor: EMPTY_VALUE,
      allyPaymentSeverity: allyPayment.severity,
      allyPaymentTitle: allyPayment.title,
      allyPaymentMessage: allyPayment.message,
      generatedAt: EMPTY_VALUE
    };
  }

  private mapCreditCertificateDetail(
    data: SalesReportCreditDetailResponse,
    fallbackRecord?: ClosingRecord
  ): CreditCertificateDetail {
    const traceability = data.history?.traceability || null;
    const values = data.values || null;
    const creditConditions = data.creditConditions || null;
    const collectionValidity = data.status?.collectionValidity || null;
    const fallback = fallbackRecord
      ? this.buildCertificateDetailFromRecord(fallbackRecord)
      : this.certificateDetail;
    const legalization = this.getLegalizationDisplay(data.status?.legalization);
    const allyPayment = this.getAllyPaymentDisplay(collectionValidity);

    return {
      contract: data.contract || fallback.contract,
      customerIdentity: data.client?.document || fallback.customerIdentity,
      customerName: data.client?.name || fallback.customerName,
      productDescription: data.product?.description || fallback.productDescription,
      imei: data.product?.imei || fallback.imei,
      agreement: this.getDistributorName(),
      totalValue: this.pickDisplayValue(
        this.formatCurrency(values?.totalDeviceValue ?? null),
        fallback.totalValue
      ),
      financedAmount: this.pickDisplayValue(
        this.formatCurrency(values?.financedAmount ?? null),
        fallback.financedAmount
      ),
      advance: this.pickDisplayValue(
        this.formatCurrency(values?.advancePaidToAdvisor ?? null),
        fallback.advance
      ),
      installments: this.pickDisplayValue(
        this.formatPlainValue(creditConditions?.installmentsCount ?? null),
        fallback.installments
      ),
      installmentValue: this.pickDisplayValue(
        this.formatCurrency(creditConditions?.installmentValue ?? null),
        fallback.installmentValue
      ),
      firstInstallmentDate: this.pickDisplayValue(
        this.formatShortDate(creditConditions?.firstInstallmentDate ?? null),
        fallback.firstInstallmentDate
      ),
      creditStatus: this.pickDisplayValue(legalization.label, fallback.creditStatus),
      creditStatusSeverity: data.status?.legalization ? legalization.severity : fallback.creditStatusSeverity,
      requestDate: this.pickDisplayValue(
        this.formatDate(traceability?.requestStartDate ?? null),
        fallback.requestDate
      ),
      lastUpdated: this.pickDisplayValue(
        this.formatDate(traceability?.lastUpdated ?? null),
        fallback.lastUpdated
      ),
      salesAdvisor: this.pickDisplayValue(
        traceability?.generatingAdvisor || EMPTY_VALUE,
        fallback.salesAdvisor
      ),
      allyPaymentSeverity: collectionValidity ? allyPayment.severity : fallback.allyPaymentSeverity,
      allyPaymentTitle: this.pickDisplayValue(allyPayment.title, fallback.allyPaymentTitle),
      allyPaymentMessage: this.pickDisplayValue(allyPayment.message, fallback.allyPaymentMessage),
      generatedAt: fallback.generatedAt
    };
  }

  private getCurrentDateTime(): string {
    return new Date().toLocaleString('es-CO');
  }

  private buildPdfFileName(contract: string): string {
    const safeContract = (contract || 'sin-contrato')
      .trim()
      .replace(/[^a-zA-Z0-9-_]/g, '-');

    return `certificado-de-venta-${safeContract}.pdf`;
  }

  private waitForNextRender(): Promise<void> {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });
  }

  private getLegalizationDisplay(status: string | null): StatusDisplay {
    return {
      label: status ? LEGALIZATION_LABELS[status] || status : EMPTY_VALUE,
      severity: status ? LEGALIZATION_SEVERITIES[status] || DEFAULT_SEVERITY : DEFAULT_SEVERITY
    };
  }

  private getAllyPaymentDisplay(status: string | null): AllyPaymentDisplay {
    return {
      label: status ? ALLY_PAYMENT_LABELS[status] || status : EMPTY_VALUE,
      severity: status ? ALLY_PAYMENT_SEVERITIES[status] || DEFAULT_SEVERITY : DEFAULT_SEVERITY,
      title: status ? ALLY_PAYMENT_TITLES[status] || '' : '',
      message: status ? ALLY_PAYMENT_MESSAGES[status] || '' : ''
    };
  }

  private getDistributorName(): string {
    return this.userData?.distributor?.name || '';
  }
}
